import { i18n } from 'lib/i18n.js'
import debounce from 'lodash/debounce'
import { buildDeliveryMessage, resolveDeliveryCountry } from 'utils/delivery'
import { formatPrice } from 'utils/general'
import Vue from 'vue'
import { mapActions, mapState } from 'vuex'

Vue.component('header-cart-item', resolve => {
  Promise.all([
    enqueueModule('quantity-selector.vue'),
    enqueueModule('vue-image.vue')
  ]).then(() => {
    resolve({
      i18n,
      name: 'header-cart-item',
      props: ['single', 'cartPage', 'maxQty'],
      data () {
        return {
          model: false,
          showQTY: true,
          engravingItem: {},
          engravingText: false
        }
      },
      watch: {
        single (_new, _old) {
          if (_old.quantity !== _new.quantity) {
            this.showQTY = false
            setTimeout(() => { this.showQTY = true }, 100)
          }
        },
        'cart.items': {
          deep: true,
          handler () {
            this.engravingItem = this.cart.items.find(e => e.properties && e.properties._key === this.single.properties._key && __GLOBAL__.engravingItem.id === e.id)
          }
        }
      },
      computed: {
        ...mapState('global', [
          'cart',
          'isMobile'
        ]),
        ...mapState('cart', [
          'cart_insoles',
          'loading',
          'delivery'
        ]),
        deliveryLine () {
          const options = this.delivery.options
          const stock = this.delivery.items[this.single.key]

          if (this.isEngravingItem || !options || !stock) {
            return false
          }

          if (stock.preorder) {
            return stock.preorderMessage || false
          }

          const message = buildDeliveryMessage(
            { ...options, manufactureTime: stock.manufactureTime },
            stock,
            resolveDeliveryCountry(options)
          )

          if (!message.deliveryMessageStatus || message.stockValue === 'Outofstock') {
            return false
          }

          return `${this.$t(`cart.delivery.${message.stockValue}`)} ${message.deliveryHtml}`
        },
        maximumText () {
          return this.$t('cart.label.maximum')
        },
        quantityText () {
          return `${this.$t('cart.label.quantity')}:`
        },
        discountedPrice () {
          return formatPrice(this.hasDiscount)
        },
        options () {
          return this.single.options_with_values
        },
        isEngravingItem () {
          return __GLOBAL__.engravingItem.id === this.single.variant_id
        },
        engravingItemQty () {
          const engravingItem = this.cart.items.find(item => item.variant_id === __GLOBAL__.engravingItem.id)
          if (engravingItem) {
            return engravingItem.quantity
          } else {
            return 0
          }
        },
        titleParts () {
          const fullTitle = (this.single.product_title || '').trim()
          if (!fullTitle) {
            return { eyebrow: '', main: '' }
          }


          const parts = fullTitle.split('- ')
          if (parts.length <= 1) {
            return { eyebrow: '', main: fullTitle }
          }

          const eyebrow = (parts[0] || '').trim()
          const main = parts.slice(1).join('- ').trim()

          return { eyebrow, main }
        }
      },
      methods: {
        ...mapActions('cart', [
          'updateItemQuantity',
          'addToCart'
        ]),
        updateQuantity: debounce(function (quantity) {
          this.updateItemQuantity({
            quantity,
            id: this.single.id
          })
        }, 450),
        async remove () {
          await this.updateItemQuantity({
            quantity: 0,
            id: this.single.id
          })

          if (this.engravingText && this.engravingItem.id) {
            await this.updateItemQuantity({
              quantity: 0,
              id: this.engravingItem.key
            })
          }
        },
        priceFormat (price) {
          return formatPrice(price)
            .replace(/\bPLN\b/g, 'zl')
            .replace(/z\u0142/g, 'zl')
        },
        option (optionItem) {
          return `${optionItem.name}: ${optionItem.value}`
        }
      },
      mounted () {
        if (this.single.properties) {
          if (this.single.properties.Text) {
            this.engravingItem = this.cart.items.find(e => e.properties && e.properties._key === this.single.properties._key && __GLOBAL__.engravingItem.id === e.id)
            this.engravingText = this.single.properties.Text
          }
        }
      },
      template: `
      <div class="header-cart__item">
        <a :href="single.url" class="header-cart__element__image relative">
          <div
            is="vue-image"
            :src="single.featured_image.url"
            :size="190"
            :alt="single.product_title"
            spinner-size="sm"
            :transparent="true"
            css="fit-contain"
          ></div>
        </a>

        <div class="header-cart__element__data">
          <div class="header-cart__element__summary">
            <div class="header-cart__element__copy">
              <a
                :href="single.url"
                class="header-cart__element__title"
              >
                <span
                  v-if="titleParts.eyebrow"
                  class="header-cart__element__title-eyebrow"
                  v-text="titleParts.eyebrow"
                ></span>
                <span
                  class="header-cart__element__title-main"
                  v-text="titleParts.main"
                ></span>
              </a>

              <div class="header-cart__element__options" v-if="single.options_with_values[0].value !== 'Default Title'">
                <p v-for="(optionItem, index) in options" :key="index" v-text="option(optionItem)"></p>
              </div>

              <div class="header-cart__element__options" v-if="engravingText">
                <p>Text: <span v-text="engravingText"></span></p>
              </div>

              <p
                v-if="deliveryLine"
                class="header-cart__element__delivery"
                v-text="deliveryLine"
              ></p>
            </div>

            <div class="header-cart__element__prices">
              <div class="header-cart__element__price" v-text="priceFormat(single.final_line_price)"></div>
              <div class="header-cart__element__price italic" v-if="engravingItem && engravingItem.id">+{{priceFormat(engravingItem.price)}}</div>
            </div>
          </div>

          <div class="header-cart__element__bottom">
            <div class="relative header-cart__element__quantity" :class="{'header-cart__element__quantity--loading': (loading === single.id)}">
              <div v-if="showQTY" class="header-cart__element__quantity__input hide">
                <span class="header-cart__element__quantity__label" v-text="quantityText"></span>
                <div
                  is="quantity-selector"
                  @change="updateQuantity"
                  :value="single.quantity"
                ></div>
              </div>
              <div v-else class="quantity-selector--loading"></div>

              <div class="header-cart__element__remove" v-if="!isEngravingItem">
                <button class="grey-5 header-cart__element__remove__btn" @click="remove">Remove</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      `
    })
  })
})
